import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface LoadingSpinnerProps {
  /** Additional CSS classes */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show fullscreen centered spinner */
  fullscreen?: boolean;
  /** Optional custom label for screen readers */
  label?: string;
}

const sizeClasses = {
  sm: 'h-4 w-4 border-2',
  md: 'h-8 w-8 border-3',
  lg: 'h-12 w-12 border-4',
};

/**
 * Loading spinner component for async loading states.
 *
 * Uses cyberpunk primary color (neon cyan #00ffff) with rotating border animation.
 * Respects prefers-reduced-motion for accessibility.
 *
 * @example
 * // Fullscreen spinner for page loading
 * <LoadingSpinner fullscreen />
 *
 * // Inline spinner with small size
 * <LoadingSpinner size="sm" />
 *
 * // Large spinner
 * <LoadingSpinner size="lg" />
 *
 * // With custom label
 * <LoadingSpinner label="Loading messages..." />
 */
export function LoadingSpinner({
  className,
  size = 'md',
  fullscreen = false,
  label,
}: LoadingSpinnerProps) {
  const { t } = useTranslation();
  const loadingText = label || t('components.loadingSpinner.loading');

  const spinner = (
    <div
      className={cn(
        'rounded-full border-primary border-t-transparent',
        'animate-spin motion-reduce:animate-none motion-reduce:border-primary/50',
        sizeClasses[size],
        className
      )}
      role="status"
      aria-label={loadingText}
    >
      <span className="sr-only">{loadingText}</span>
    </div>
  );

  if (fullscreen) {
    return <div className="flex h-dvh items-center justify-center">{spinner}</div>;
  }

  return spinner;
}

export default LoadingSpinner;
